import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { extractApiError } from "../lib/apiErrors";
import { blogErrorMessage } from "../lib/blogErrors";
import { formatBlogDate } from "../lib/blogDates";
import { useT, useLanguage, useLocalized } from "../lib/LanguageContext";
import useAdminLoad from "../hooks/useAdminLoad";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import LoadFailure from "../components/ui/LoadFailure";
import Pager from "../components/ui/Pager";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, Pencil, ExternalLink, FileSearch } from "lucide-react";
import { BLOG_LANGS } from "./blogPayload";
import HqBlogEditor from "./HqBlogEditor";
import HqBlogPreview from "./HqBlogPreview";
import { useScreenWrites } from "./screenWrites";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const BLOG_API = "/super-admin/hq/blog";
const SELECT_CLASS = "bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700";

export default function HqBlog() {
  const t = useT("superadmin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const loc = useLocalized();
  // Reading is data.content.read; writing, publishing and deleting are settings.write. Without it
  // a post opens in the preview, never in the editor.
  const { manage: canWrite } = useScreenWrites("blog");

  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("updated_desc");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, items: [] });
  // The post an overlay is open on: `{}` = a new post, a full post = editing it.
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  // The server is asked once the operator stops typing; a new query starts at page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    const r = await api.get(BLOG_API, {
      params: { q, status, sort, limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE },
    });
    setData({ total: r.data.total, items: r.data.items });
  }, [q, status, sort, page]);
  const { loading, loadError, reload } = useAdminLoad(load);

  // The list omits bodies, so every overlay starts from the full post.
  const openFull = async (id, show) => {
    try {
      show((await api.get(`${BLOG_API}/${id}`)).data);
    } catch (err) {
      toast.error(blogErrorMessage(err, t));
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("hqblog.confirmDelete"))) return;
    try {
      await api.delete(`${BLOG_API}/${id}`);
      reload();
    } catch (err) {
      toast.error(blogErrorMessage(err, t));
    }
  };
  const togglePublish = async (p) => {
    try {
      await api.put(`${BLOG_API}/${p.post_id}/published`, { published: !p.published });
    } catch (err) {
      toast.error(blogErrorMessage(err, t));
    }
    reload();
  };

  const onFilter = (setter) => (e) => { setter(e.target.value); setPage(1); };
  const date = (iso) => formatBlogDate(iso, lang) || t("hqblog.never");

  return (
    <div data-testid="hq-blog">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("hqblog.title")}</h1>
          <p className="text-slate-500">{t("hqblog.subtitle")}</p>
        </div>
        {canWrite && (
          <Button onClick={() => setEditing({})} className="bg-sol-gold text-black hover:bg-amber-200" data-testid="new-blog">
            <Plus className="w-4 h-4 mr-2" /> {t("hqblog.new")}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("hqblog.search")}
          aria-label={t("hqblog.search")}
          className="bg-white border-slate-200 max-w-xs"
          data-testid="blog-search"
        />
        <select value={status} onChange={onFilter(setStatus)} aria-label={t("hqblog.filterStatus")} className={SELECT_CLASS} data-testid="blog-status-filter">
          <option value="all">{t("hqblog.statusAll")}</option>
          <option value="published">{t("hqblog.statusPublished")}</option>
          <option value="draft">{t("hqblog.statusDraft")}</option>
        </select>
        <select value={sort} onChange={onFilter(setSort)} aria-label={t("hqblog.sortBy")} className={SELECT_CLASS} data-testid="blog-sort">
          <option value="updated_desc">{t("hqblog.sortUpdated")}</option>
          <option value="published_desc">{t("hqblog.sortPublished")}</option>
          <option value="created_desc">{t("hqblog.sortCreated")}</option>
        </select>
        <span className="text-xs text-slate-400 ml-auto tabular-nums" data-testid="blog-total">
          {t("hqblog.total", { total: data.total })}
        </span>
      </div>

      <LoadFailure
        message={loadError ? extractApiError(loadError, tc("errors.loadFailed"), tc) : ""}
        onRetry={reload}
        retryLabel={t("hqblog.retry")}
        testId="blog-load-error"
      />

      {!loadError && (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400 tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">{t("hqblog.colTitle")}</th>
                <th className="text-left px-4 py-3">{t("hqblog.colLanguages")}</th>
                <th className="text-left px-4 py-3">{t("hqblog.colStatus")}</th>
                <th className="text-left px-4 py-3">{t("hqblog.colPublished")}</th>
                <th className="text-left px-4 py-3">{t("hqblog.colUpdated")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.post_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`blog-row-${p.slug}`}>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => openFull(p.post_id, canWrite ? setEditing : setPreviewing)} className="text-left text-slate-800 hover:underline">
                      {loc(p, "title") || p.slug}
                    </button>
                    <div><code className="text-xs text-amber-600">{p.slug}</code></div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {BLOG_LANGS.filter((l) => (p.languages || []).includes(l)).map((l) => (
                        <span key={l} className="text-[10px] font-semibold rounded bg-slate-100 text-slate-600 px-1.5 py-0.5" data-testid={`lang-badge-${p.slug}-${l}`}>
                          {l.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {canWrite ? (
                      <button type="button" onClick={() => togglePublish(p)} className={`text-xs rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${p.published ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`} data-testid={`publish-${p.slug}`}>
                        {p.published ? <><Eye className="w-3 h-3" /> {t("hqblog.published")}</> : <><EyeOff className="w-3 h-3" /> {t("hqblog.draft")}</>}
                      </button>
                    ) : (
                      <span className={`text-xs rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${p.published ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`} data-testid={`status-${p.slug}`}>
                        {p.published ? <><Eye className="w-3 h-3" /> {t("hqblog.published")}</> : <><EyeOff className="w-3 h-3" /> {t("hqblog.draft")}</>}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{date(p.published_at)}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{date(p.updated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3 text-slate-400">
                      {canWrite && <button type="button" onClick={() => openFull(p.post_id, setEditing)} title={t("hqblog.edit")} aria-label={t("hqblog.edit")} data-testid={`edit-${p.slug}`}><Pencil className="w-3.5 h-3.5" /></button>}
                      <button type="button" onClick={() => openFull(p.post_id, setPreviewing)} title={t("hqblog.preview")} aria-label={t("hqblog.preview")} data-testid={`preview-${p.slug}`}><FileSearch className="w-3.5 h-3.5" /></button>
                      {p.published && (
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" title={t("hqblog.openPublic")} aria-label={t("hqblog.openPublic")} data-testid={`open-${p.slug}`}><ExternalLink className="w-3.5 h-3.5" /></a>
                      )}
                      {canWrite && <button type="button" onClick={() => del(p.post_id)} className="text-rose-400" title={t("hqblog.delete")} aria-label={t("hqblog.delete")} data-testid={`del-${p.slug}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan="6" className="text-center px-4 py-10 text-slate-400">{t("hqblog.empty")}</td></tr>
              )}
            </tbody>
          </table>
          {data.total > PAGE_SIZE && (
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={data.total}
              shown={data.items.length}
              onPage={setPage}
              infoText={(v) => t("hqblog.pagerInfo", v)}
              prevLabel={t("hqblog.pagerPrev")}
              nextLabel={t("hqblog.pagerNext")}
              testId="blog-pager"
              tone="slate"
            />
          )}
        </div>
      )}

      {canWrite && editing !== null && (
        <HqBlogEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); reload(); }}
        />
      )}
      {previewing !== null && <HqBlogPreview post={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}
